import {
  PAGE_LAYOUT_SLOTS,
  type PageAudience,
  type PageSectionEntry,
  type PageSectionList,
  type PickableStorefrontPage,
  STOREFRONT_BOX_LISTS,
  STOREFRONT_FILLED_SECTIONS,
  STOREFRONT_HOME_LISTS,
  STOREFRONT_REQUIRED_SECTIONS,
  STOREFRONT_SECTION_VARIANTS,
  type StorefrontFeatureKey,
  type StorefrontSectionId,
  sectionListFor,
} from "@loot-os/api-contract";

import type { PreviewPageId } from "./themePreview";

/** A row of the Pages page's list that holds pages rather than being one. */
export type StorefrontPagesGroupId = "boxesGroup" | "battlesGroup" | "forgeGroup";

/** An entry of the Pages page's list: a part of the store every page shares, a group, or a page. */
export type StorefrontPagesEntryId =
  | "headerFooter"
  | "chat"
  | StorefrontPagesGroupId
  | PreviewPageId;

/** One row of the Pages page's list. */
export interface StorefrontPagesEntry {
  id: StorefrontPagesEntryId;
  label: string;
  /** The page the preview shows while the entry is open. */
  preview: PreviewPageId;
  /** The feature that has to be on for shoppers to see it; off, it is listed under Not shown. */
  feature?: StorefrontFeatureKey;
  /** The storefront section where that feature is switched on. */
  featurePath?:
    | "/tenants/$tenantId/storefront/battles"
    | "/tenants/$tenantId/storefront/chat"
    | "/tenants/$tenantId/storefront/forge";
  /**
   * The pages a group holds, listed under it. A group's feature is theirs: off, the group moves to
   * Not shown with every page in it.
   */
  children?: readonly StorefrontPagesEntry[];
}

/** What every page shares: the header, the footer and the chat panel. */
export const SITE_WIDE_ENTRIES: readonly StorefrontPagesEntry[] = [
  { id: "headerFooter", label: "Header and footer", preview: "home" },
  {
    id: "chat",
    label: "Chat",
    preview: "home",
    feature: "chat",
    featurePath: "/tenants/$tenantId/storefront/chat",
  },
];

const BATTLES_FEATURE = {
  feature: "battles",
  featurePath: "/tenants/$tenantId/storefront/battles",
} as const;

const FORGE_FEATURE = {
  feature: "forge",
  featurePath: "/tenants/$tenantId/storefront/forge",
} as const;

/** The store's pages, in the order a shopper meets them; a feature's pages as one group. */
export const PAGE_ENTRIES: readonly StorefrontPagesEntry[] = [
  { id: "home", label: "Home", preview: "home" },
  {
    id: "boxesGroup",
    label: "Boxes",
    preview: "boxes",
    children: [
      { id: "boxes", label: "Catalog", preview: "boxes" },
      { id: "box", label: "Box page", preview: "box" },
    ],
  },
  {
    id: "battlesGroup",
    label: "Battles",
    preview: "battles",
    ...BATTLES_FEATURE,
    children: [
      { id: "battles", label: "Lobby", preview: "battles", ...BATTLES_FEATURE },
      { id: "battleCreate", label: "Create battle", preview: "battleCreate", ...BATTLES_FEATURE },
      { id: "battle", label: "Battle room", preview: "battle", ...BATTLES_FEATURE },
    ],
  },
  {
    id: "forgeGroup",
    label: "Forge",
    preview: "forge",
    ...FORGE_FEATURE,
    children: [
      { id: "forge", label: "Browse", preview: "forge", ...FORGE_FEATURE },
      { id: "forgeBuilder", label: "Builder", preview: "forgeBuilder", ...FORGE_FEATURE },
      { id: "forgeMine", label: "My boxes", preview: "forgeMine", ...FORGE_FEATURE },
    ],
  },
  { id: "inventory", label: "Inventory", preview: "inventory" },
  { id: "account", label: "Account", preview: "account" },
  { id: "fairness", label: "Fairness", preview: "fairness" },
  { id: "login", label: "Sign-in", preview: "login" },
];

/** Every entry of the list, a group's pages after it, so one can be found by id. */
export function allPagesEntries(): StorefrontPagesEntry[] {
  return [...SITE_WIDE_ENTRIES, ...PAGE_ENTRIES].flatMap((entry) => [
    entry,
    ...(entry.children ?? []),
  ]);
}

/**
 * The list's groups: what every page shares, the pages shoppers see, and the ones they don't. A
 * group of pages stays whole, under Not shown with its feature off.
 */
export function groupPagesEntries(
  hasFeature: (key: StorefrontFeatureKey) => boolean,
): { title: string; entries: StorefrontPagesEntry[] }[] {
  const shown = (entry: StorefrontPagesEntry) => !entry.feature || hasFeature(entry.feature);
  const groups = [
    { title: "Site-wide", entries: SITE_WIDE_ENTRIES.filter(shown) },
    { title: "Pages", entries: PAGE_ENTRIES.filter(shown) },
    {
      title: "Not shown",
      entries: [...SITE_WIDE_ENTRIES, ...PAGE_ENTRIES].filter((entry) => !shown(entry)),
    },
  ];
  return groups.filter((group) => group.entries.length > 0);
}

/** Home's sections as a tenant reads them. */
export const HOME_SECTION_LABELS: Record<StorefrontSectionId<"home">, string> = {
  hero: "Hero",
  liveHits: "Live hits",
  recentOpens: "Recent opens",
  featuredBoxes: "Featured boxes",
  battles: "Battles",
  forge: "Forge",
  promoCards: "Promo cards",
  products: "Products",
  reviews: "Reviews",
  promises: "Promises",
  faq: "FAQ",
  closing: "Closing band",
};

/** The box page's sections, as the Pages page names them. */
const BOX_SECTION_LABELS: Record<StorefrontSectionId<"box">, string> = {
  heading: "Heading and tools",
  opener: "Opener",
  volatility: "Volatility",
  recentPulls: "Recent pulls",
  bestPull: "Best pull",
  odds: "Odds",
  drops: "Every item",
};

/** A section a layout shows, as the Pages page lists it. */
export interface LayoutSection {
  id: string;
  label: string;
  /** The look it is drawn in ("Rail"), or empty where the section has one look. */
  variant: string;
  /** The look's id, its section's default where the list names none (`"rail"`). */
  variantId: string;
  /** Every layout of the page has to show it. */
  required: boolean;
  /** The feature it shows only while on. */
  feature?: StorefrontFeatureKey;
}

/** `"rail"` → `"Rail"`: a variant id as the list shows it. */
function variantLabel(id: string): string {
  return id.charAt(0).toUpperCase() + id.slice(1);
}

/** Home's sections that show only while a feature is on. */
const HOME_SECTION_FEATURES: Partial<Record<StorefrontSectionId<"home">, StorefrontFeatureKey>> = {
  battles: "battles",
  forge: "forge",
};

/**
 * The sections `layout` shows on `page`, top to bottom, for `audience`. Home's come from its
 * lists (the entries for that audience), less the ones the storefront has nothing to fill with yet
 * (live hits, products, reviews, promises: drawn in Storybook, empty on a store), so the list
 * never names a section a shopper can't see; one joins once the storefront fills it
 * (`STOREFRONT_FILLED_SECTIONS`). The closing band is filled for a visitor alone, with its call to
 * sign up. The box page's come from its lists the same way (recent pulls joins once a feed fills
 * it). The other pages a store can pick a layout for have no list yet.
 */
export function layoutSections(
  page: PickableStorefrontPage,
  layout: string,
  audience: PageAudience = "signedIn",
): LayoutSection[] {
  if (page === "home") {
    const filled: readonly string[] =
      audience === "signedOut"
        ? [...STOREFRONT_FILLED_SECTIONS.home, "closing"]
        : STOREFRONT_FILLED_SECTIONS.home;
    const stored = STOREFRONT_HOME_LISTS[layout as keyof typeof STOREFRONT_HOME_LISTS];
    if (!stored) return [];
    return listedSections(sectionListFor(stored, audience), filled).map((entry) => ({
      id: entry.section,
      label: HOME_SECTION_LABELS[entry.section],
      variant: entry.variant ? variantLabel(entry.variant) : "",
      variantId: entry.variant ?? STOREFRONT_SECTION_VARIANTS.home[entry.section][0],
      required: (STOREFRONT_REQUIRED_SECTIONS.home ?? []).includes(entry.section),
      feature: HOME_SECTION_FEATURES[entry.section],
    }));
  }
  if (page === "box") {
    const stored = STOREFRONT_BOX_LISTS[layout as keyof typeof STOREFRONT_BOX_LISTS];
    if (!stored) return [];
    return listedSections(sectionListFor(stored, audience), STOREFRONT_FILLED_SECTIONS.box).map(
      (entry) => ({
        id: entry.section,
        label: BOX_SECTION_LABELS[entry.section],
        variant: entry.variant ? variantLabel(entry.variant) : "",
        variantId: entry.variant ?? STOREFRONT_SECTION_VARIANTS.box[entry.section][0],
        required: (STOREFRONT_REQUIRED_SECTIONS.box ?? []).includes(entry.section),
      }),
    );
  }
  return [];
}

/** A list's entries in its layout's slot order, less those the storefront doesn't fill yet. */
function listedSections<Id extends string>(
  list: PageSectionList<Id>,
  filled: readonly string[],
): PageSectionEntry<Id>[] {
  const slots = list.slots as Readonly<Record<string, readonly PageSectionEntry<Id>[]>>;
  return PAGE_LAYOUT_SLOTS[list.layout]
    .flatMap((slot) => slots[slot] ?? [])
    .filter((entry) => filled.includes(entry.section));
}

/**
 * The sections a page's current layout shows `audience` that `next` leaves out, by label: what the
 * Pages page warns about, for whichever of signed in and signed out it is showing.
 */
export function sectionsLeftOut(
  page: PickableStorefrontPage,
  current: string,
  next: string,
  audience: PageAudience = "signedIn",
): string[] {
  const kept = new Set(layoutSections(page, next, audience).map((section) => section.id));
  return layoutSections(page, current, audience)
    .filter((section) => !kept.has(section.id))
    .map((section) => section.label);
}
